import eventemitter from '@/lib/eventEmitter';

/**
 * Emitted when a request to a workspace reveals that the workspace is not running.
 *
 * A paused workspace has no pods, so Traefik catches the upstream 502/504 and hands the request to
 * the auto-start handler, which answers on the workspace's behalf. That handler replies 202 (not an
 * error status) for API requests, so a caller that only checks for failures sees a "success" whose
 * body is a status message rather than the data it asked for.
 */
export const PAUSED_WORKSPACE_DETECTED_EVENT = 'paused-workspace-detected';

export type PausedWorkspaceDetectedPayload = {
  // Present when the response identified the workspace; the guard falls back to the current
  // workspace when it is absent.
  workspaceId?: string;
  // True when auto-start already kicked off a resume, so the workspace is on its way back up.
  resuming: boolean;
  message: string;
};

const WORKSPACE_STARTING_RE = /Workspace\s+([0-9a-f-]{36})\s+is starting/i;
const AUTO_START_DISABLED_RE = /auto start is not enabled for this workspace/i;
const TRANSIENT_ERROR_RE = /Workspace encountered \d+ error/i;

function readMessage(body: unknown): string | undefined {
  if (typeof body === 'string') {
    try {
      const parsed = JSON.parse(body);
      return typeof parsed?.message === 'string' ? parsed.message : undefined;
    } catch {
      return undefined;
    }
  }
  if (body && typeof body === 'object' && typeof (body as { message?: unknown }).message === 'string') {
    return (body as { message: string }).message;
  }
  return undefined;
}

/**
 * Recognizes an auto-start handler reply. Returns undefined for anything else, including ordinary
 * successful workspace responses.
 */
export function detectPausedWorkspaceResponse(
  status: number,
  body: unknown
): PausedWorkspaceDetectedPayload | undefined {
  const message = readMessage(body);
  if (!message) {
    return undefined;
  }

  if (status === 202) {
    const starting = WORKSPACE_STARTING_RE.exec(message);
    if (starting) {
      return { workspaceId: starting[1], resuming: true, message };
    }
    // The handler also answers 202 for a passthrough 502/504. That is not necessarily a pause, so
    // it is deliberately not reported as one.
    if (TRANSIENT_ERROR_RE.test(message)) {
      return undefined;
    }
    return undefined;
  }

  if (status === 500 && AUTO_START_DISABLED_RE.test(message)) {
    // Paused with auto-resume off: nothing is starting, the user must resume explicitly.
    return { resuming: false, message };
  }

  return undefined;
}

export function emitPausedWorkspaceDetected(payload: PausedWorkspaceDetectedPayload) {
  eventemitter.emit(PAUSED_WORKSPACE_DETECTED_EVENT, payload);
}

/**
 * Inspects a response and announces a paused workspace if that is what it turned out to be.
 * Returns true when the response was an auto-start reply rather than real data.
 */
export function reportIfPausedWorkspaceResponse(status: number, body: unknown): boolean {
  const detected = detectPausedWorkspaceResponse(status, body);
  if (!detected) {
    return false;
  }
  emitPausedWorkspaceDetected(detected);
  return true;
}
